import { addTimes, formatSeconds, parseTimeToSeconds, intervalToString } from "@/lib/utils";
import { personResultGender, teamResultGender, type ResultGender } from "@/lib/gender";
import type { TeamMemberInfo, EventResult, EventResultEntry, ResultTiming } from "@/types/results";
import type { RunSegmentConfig } from "@shared/gpxTrack";

// ---------------------------------------------------------------------------
// Domain Constants
// ---------------------------------------------------------------------------

/** Default number of questions per quiz event. */
export const DEFAULT_QUESTION_COUNT = 10;

/** Fallback city name when location is unknown. */
export const UNKNOWN_CITY = "Unknown";

/** Maximum gap in days between events to count as a streak. */
const MAX_STREAK_GAP_DAYS = 90;

/** Similarity threshold for fuzzy team name matching. */
export const TEAM_SIMILARITY_THRESHOLD = 0.6;

/** Similarity threshold for fuzzy person name matching. */
export const NAME_SIMILARITY_THRESHOLD = 0.8;

// ---------------------------------------------------------------------------
// Shared Result Building
// ---------------------------------------------------------------------------

interface QuizrunInfo {
  id: number;
  number: number | null;
  language: string;
  date_start: string;
  title?: string | null;
}

/**
 * Builds an EventResultEntry from a matched EventResult and quizrun metadata.
 * Shared between participant and team result fetchers.
 */
export function buildEventResultEntry(
  qr: QuizrunInfo,
  result: EventResult,
  place: number,
  totalResults: number,
  overrides?: { city?: string; country?: string; teamName?: string },
): EventResultEntry {
  return {
    eventId: String(qr.id),
    eventName: qr.title,
    eventDate: qr.date_start,
    locationId: result.locationId,
    participationId: result.participationId,
    city: overrides?.city ?? result.city ?? UNKNOWN_CITY,
    country: overrides?.country ?? result.country ?? "",
    place,
    totalResults,
    category: result.category,
    isDNF: result.isDNF,
    teamId: result.teamId,
    teamName: overrides?.teamName,
    format: result.format,
    totalAnswers: result.totalAnswers ?? 0,
    answerGroups: result.answerGroups,
    questionCount: result.questionAnswers?.length || DEFAULT_QUESTION_COUNT,
    totalTime: result.totalTime,
    parts: result.parts,
    thinkTimes: result.thinkTimes,
    totalRunTime: result.totalRunTime,
    totalThinkTime: result.totalThinkTime,
    totalScore: result.totalScore,
  };
}

/** Sort EventResultEntry array by date descending (newest first). */
export function sortResultsByDateDesc(results: EventResultEntry[]): void {
  results.sort(
    (a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime(),
  );
}

// ---------------------------------------------------------------------------
// DNF Detection
// ---------------------------------------------------------------------------

/**
 * Determine "Did Not Finish" from positional per-part time arrays.
 *
 * Each event has one array slot per part (e.g. two laps). A finished result
 * fills every part slot; a DNF leaves at least one slot empty, encoded as an
 * explicit null — e.g. times = ['0:13:38', null] (ran lap 1, skipped lap 2).
 *
 * A part counts as reached if it has a value in ANY of the supplied arrays
 * (times / run_times / think_times), so a result timed only by run+think isn't
 * flagged DNF just because `times` is absent. Length-1 arrays with no null
 * represent genuine single-part historical events and are treated as finished.
 */
export const isResultDNF = (
  ...partArrays: Array<ReadonlyArray<string | null> | null | undefined>
): boolean => {
  const arrays = partArrays.filter(
    (a): a is ReadonlyArray<string | null> => Array.isArray(a) && a.length > 0,
  );
  if (arrays.length === 0) return false;
  const partCount = Math.max(...arrays.map((a) => a.length));
  for (let i = 0; i < partCount; i++) {
    const reached = arrays.some((a) => {
      const v = a[i];
      return v != null && String(v).trim() !== "";
    });
    if (!reached) return true;
  }
  return false;
};

// ---------------------------------------------------------------------------
// Row ownership
// ---------------------------------------------------------------------------

/**
 * Whether `personId` played in this result row.
 *
 * A team is collapsed to a single row (see fetchEventResultsFromDatabase —
 * "one row per team"), and that row's `personId` is whichever member the RPC
 * happened to return first, not a designated captain. The full roster lives on
 * `teamMembers`, so both have to be consulted or teammates other than the
 * arbitrary representative are treated as strangers to their own result.
 */
export const resultIncludesPerson = (
  result: Pick<EventResult, "personId" | "teamMembers">,
  personId: number | null,
): boolean => {
  if (personId === null) return false;
  return (
    result.personId === personId ||
    !!result.teamMembers?.some((m) => m.id === personId)
  );
};

// ---------------------------------------------------------------------------
// Time Color Constants
// ---------------------------------------------------------------------------

export const TIME_COLORS = {
  run: "text-secondary",
  think: "text-primary",
} as const;

// ---------------------------------------------------------------------------
// Gender Resolution
// ---------------------------------------------------------------------------

/**
 * Result-row gender: a team from its members (see teamResultGender), a solo
 * runner from their stored person.gender.
 */
export const resolveGender = (
  isTeam: boolean,
  teamMembers: TeamMemberInfo[] | undefined,
  personGender: string | undefined
): ResultGender | undefined =>
  isTeam
    ? teamResultGender((teamMembers ?? []).map((m) => m.gender))
    : personResultGender(personGender);

// ---------------------------------------------------------------------------
// Total Time Calculation
// ---------------------------------------------------------------------------

/** Missing required times keep a total null; partial displays opt into summing recorded times. */
export const sumTimes = (
  times: ReadonlyArray<string | null>,
  requireAll = true,
): string | null => {
  if (requireAll && times.some((time) => time === null)) return null;
  const recorded = times.filter((time): time is string => time !== null);
  if (recorded.length === 0) return null;
  if (recorded.length === 1) return recorded[0];
  return formatSeconds(recorded.reduce((sum, time) => sum + parseTimeToSeconds(time), 0));
};

interface RawResultTiming {
  times?: ReadonlyArray<string | null> | null;
  run_times?: ReadonlyArray<string | null> | null;
  think_times?: ReadonlyArray<string | null> | null;
  total_time?: string | null;
}

/** Stored run slots follow think ordinals, not run ordinals. Background slots are never runs. */
export function calculateResultTiming(
  result: RawResultTiming,
  segments: ReadonlyArray<RunSegmentConfig>,
): ResultTiming & { isDNF: boolean } {
  const times = (result.times ?? []).map(intervalToString);
  const runs = (result.run_times ?? []).map(intervalToString);
  const thinks = (result.think_times ?? []).map(intervalToString);
  const thinkConfigs = segments.filter((segment) => segment.type === "think");
  const legacyCombined = times.length > 0 && !thinkConfigs.some((segment) => segment.background);
  const legacyParts = legacyCombined && runs.length === 0 && thinks.length === 0;
  const runSlots: (number | null)[] = [];
  let thinkIndex = 0;
  for (const segment of segments) {
    if (segment.type === "run") {
      runSlots.push(null);
    } else {
      if (!segment.background && runSlots.length > 0 && runSlots[runSlots.length - 1] === null) {
        runSlots[runSlots.length - 1] = thinkIndex;
      }
      thinkIndex++;
    }
  }

  // Historical combined-only rows can predate the current/default segment
  // configuration. Their explicit positional arrays still define their parts.
  const slots = legacyParts || segments.length === 0
    ? Array.from({ length: Math.max(times.length, runs.length, thinks.length) }, (_, i) => i)
    : runSlots;
  const scoredThink = (slot: number) =>
    !thinkConfigs[slot]?.background && thinkConfigs[slot]?.length !== 0;
  const thinkTimes = (legacyParts || segments.length === 0
    ? Array.from({ length: Math.max(times.length, runs.length, thinks.length) }, (_, i) => i)
    : thinkConfigs.map((_, i) => i))
    .filter(scoredThink)
    .map((slot) => thinks[slot] ?? null);

  const parts = slots.map((slot) => {
    const runTime = slot === null ? null : runs[slot] ?? null;
    const thinkTime = slot !== null && scoredThink(slot) ? thinks[slot] ?? null : null;
    const combined = slot !== null && !scoredThink(slot)
      ? runTime
      : addTimes(runTime, thinkTime);
    return {
      runTime,
      thinkTime,
      time: combined ?? (legacyCombined && slot !== null ? times[slot] ?? null : null),
    };
  });
  const hasTiming = times.length > 0 || runs.length > 0 || thinks.length > 0;
  const isDNF = hasTiming && isResultDNF(
    parts.map((part) => part.time),
    parts.map((part) => part.runTime),
    parts.map((part) => part.thinkTime),
  );
  const totalRunTime = sumTimes(parts.map((part) => part.runTime));
  const totalThinkTime = sumTimes(thinkTimes);
  const fullyTimed = parts.every((part) => part.runTime !== null) &&
    thinkTimes.every((time) => time !== null);
  const decomposedTotal = fullyTimed
    ? sumTimes([...parts.map((part) => part.runTime), ...thinkTimes])
    : null;
  const totalTime = isDNF ? null : decomposedTotal ??
    (legacyCombined ? sumTimes(parts.map((part) => part.time)) : null) ??
    (!hasTiming ? intervalToString(result.total_time) : null);

  return { parts, thinkTimes, totalRunTime, totalThinkTime, totalTime, isDNF };
}

// ---------------------------------------------------------------------------
// Result Statistics & Achievements (shared by PlayerResults & TeamResults)
// ---------------------------------------------------------------------------

/** Minimal shape needed for statistics computation. */
interface ResultForStats {
  totalScore?: string | null;
  totalTime?: string | null;
  totalAnswers: number;
  place: number;
  city: string;
  eventDate: string;
}

export interface Achievements {
  subzero: boolean;
  firstSteps: boolean;
  milestone10: boolean;
  milestone100: boolean;
  firstPlace: boolean;
  podium: boolean;
  traveler: boolean;
  perfectScore: boolean;
  locations: number;
  totalAnswers100: boolean;
  totalAnswers500: boolean;
  maxAnswers10: boolean;
  fastestUnder10Min: boolean;
  fastestUnder5Min: boolean;
}

export interface Statistics {
  bestScore: string;
  fastestTime: string;
  mostCorrect: number;
  locationCount: number;
  streak: number;
}

/** Longest consecutive-event streak (within 90-day gaps). */
export const calculateStreak = (results: ResultForStats[]): number => {
  if (results.length === 0) return 0;

  const sortedDates = results
    .map((r) => new Date(r.eventDate))
    .sort((a, b) => b.getTime() - a.getTime());

  let maxStreak = 1;
  let currentStreak = 1;

  for (let i = 1; i < sortedDates.length; i++) {
    const daysDiff = Math.floor(
      (sortedDates[i - 1].getTime() - sortedDates[i].getTime()) /
        (1000 * 60 * 60 * 24),
    );
    if (daysDiff <= MAX_STREAK_GAP_DAYS) {
      currentStreak++;
      maxStreak = Math.max(maxStreak, currentStreak);
    } else {
      currentStreak = 1;
    }
  }

  return maxStreak;
};

/** Compute achievement flags from a list of event results. */
export const computeAchievements = (results: ResultForStats[]): Achievements => {
  const totalCorrectAnswers = results.reduce((sum, r) => sum + (r.totalAnswers ?? 0), 0);
  const maxAnswersInEvent = results.length > 0
    ? Math.max(...results.map((r) => r.totalAnswers ?? 0))
    : 0;
  const fastestTimeSeconds = results.reduce((fastest, r) => {
    if (!r.totalTime) return fastest;
    const seconds = parseTimeToSeconds(r.totalTime);
    return seconds < fastest ? seconds : fastest;
  }, Infinity);
  const uniqueCities = new Set(results.map((r) => r.city)).size;

  return {
    subzero: results.some((r) => {
      if (!r.totalScore) return false;
      return r.totalScore.startsWith("-");
    }),
    firstSteps: results.length >= 1,
    milestone10: results.length >= 10,
    milestone100: results.length >= 100,
    firstPlace: results.some((r) => r.place === 1),
    podium: results.some((r) => r.place <= 3),
    traveler: uniqueCities >= 5,
    perfectScore: results.some((r) => r.totalAnswers === DEFAULT_QUESTION_COUNT),
    locations: uniqueCities,
    totalAnswers100: totalCorrectAnswers >= 100,
    totalAnswers500: totalCorrectAnswers >= 500,
    maxAnswers10: maxAnswersInEvent === DEFAULT_QUESTION_COUNT,
    fastestUnder10Min: fastestTimeSeconds < 600,
    fastestUnder5Min: fastestTimeSeconds < 300,
  };
};

/** Find the time-string with the smallest parsed value, or fallback. */
const minTimeString = (
  results: ResultForStats[],
  getter: (r: ResultForStats) => string | null | undefined,
  fallback: string,
): string => {
  let best = fallback;
  let bestSeconds = Infinity;
  for (const r of results) {
    const val = getter(r);
    if (!val) continue;
    const seconds = parseTimeToSeconds(val);
    if (seconds < bestSeconds) {
      bestSeconds = seconds;
      best = val;
    }
  }
  return best;
};

/** Compute summary statistics from a list of event results. */
export const computeStatistics = (results: ResultForStats[]): Statistics => ({
  bestScore: minTimeString(results, (r) => r.totalScore, "—"),
  fastestTime: minTimeString(results, (r) => r.totalTime, "—"),
  mostCorrect: results.length > 0
    ? Math.max(...results.map((r) => r.totalAnswers ?? 0))
    : 0,
  locationCount: new Set(results.map((r) => r.city)).size,
  streak: calculateStreak(results),
});

// ---------------------------------------------------------------------------
// Place Calculation
// ---------------------------------------------------------------------------

/**
 * Sort event results by totalScore and find the place of a specific entry.
 * Only finishers (hasResult && !isDNF) are ranked; DNF entries are excluded
 * from both the ordering and the totalResults denominator, and resolve to
 * place 0 (callers render "DNF" instead of a number).
 */
export const calculatePlace = (
  results: EventResult[],
  predicate: (r: EventResult) => boolean,
): { place: number; totalResults: number } => {
  const finishers = results.filter((r) => r.hasResult && !r.isDNF && r.totalScore !== null);
  const sorted = [...finishers].sort(
    (a, b) => parseTimeToSeconds(a.totalScore) - parseTimeToSeconds(b.totalScore),
  );
  const place = sorted.findIndex(predicate) + 1;
  return { place, totalResults: finishers.length };
};
